import { useTranslation } from 'react-i18next';
import { MapPinOff, WifiOff } from 'lucide-react';
import type { MapFailureReason } from '../../utils/mapDiagnostics';

type Props = {
  reason: MapFailureReason;
  /** Not offered for `config` failures, where retrying can't help. */
  onRetry: () => void;
  onBackToList: () => void;
};

/**
 * Fallback shown in place of a map that couldn't load. Copy and the retry
 * button vary by failure reason; "Back to list" is always available so the
 * user is never stuck on a dead map.
 */
export const MapFailureOverlay: React.FC<Props> = ({ reason, onRetry, onBackToList }) => {
  const { t } = useTranslation('home');

  const copyKey =
    reason === 'offline' || reason === 'config' || reason === 'data' ? reason : 'generic';
  const canRetry = reason !== 'config';
  const Icon = reason === 'offline' ? WifiOff : MapPinOff;

  return (
    <div
      role="alert"
      className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-card px-6 text-center"
      // Promote to own GPU layer; Chrome mobile otherwise composites the
      // Google Maps canvas above CSS-positioned siblings.
      style={{ transform: 'translateZ(0)' }}
    >
      <Icon className="h-10 w-10 text-muted-foreground" />
      <h2 className="text-lg font-semibold">{t(`feed.map.failed.${copyKey}.title`)}</h2>
      <p className="max-w-xs text-sm text-muted-foreground">
        {t(`feed.map.failed.${copyKey}.body`)}
      </p>
      <div className="mt-2 flex gap-3">
        <button
          type="button"
          onClick={onBackToList}
          className="btn-outline rounded-lg border-brand-primary bg-transparent px-4 py-2 text-sm font-medium text-brand-primary transition-colors hover:bg-brand-primary/5"
        >
          {t('feed.map.failed.backToList')}
        </button>
        {canRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-lg bg-[var(--ion-color-primary)] px-4 py-2 text-sm font-medium text-white shadow-md transition-transform active:scale-95"
          >
            {t('feed.map.failed.retry')}
          </button>
        )}
      </div>
    </div>
  );
};
